import { useEffect, useMemo, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Play, Info, Loader2, CheckCircle2, AlertTriangle, XCircle,
  Shield, Clock, Users, Calendar, Award, ExternalLink,
} from "lucide-react";

// Leave Simulation tab (read-only).
// Cautious UI wording per Tweak 1: "Looks safe to approve" / "Needs review" / "Likely should decline".

const VERDICT_META = {
  approve: { label: "Looks safe to approve", colour: "text-emerald-700",
             bg: "bg-emerald-50", border: "border-emerald-200", Icon: CheckCircle2 },
  warn:    { label: "Needs review",          colour: "text-amber-700",
             bg: "bg-amber-50", border: "border-amber-200", Icon: AlertTriangle },
  decline: { label: "Likely should decline", colour: "text-rose-700",
             bg: "bg-rose-50", border: "border-rose-200", Icon: XCircle },
};

const ICON_BY_TYPE = {
  coverage: Shield, leave: Clock, skill: Award,
  concurrency: Users, notice: Clock, setup: Info, calendar: Calendar,
};

function isoToday() { return new Date().toISOString().slice(0, 10); }
function isoDaysFromToday(n) {
  const d = new Date(); d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(`${iso}T12:00:00Z`).toLocaleDateString("en-GB", {
    weekday: "short", day: "2-digit", month: "short", year: "numeric",
  });
}

export default function LeaveSimulationTab() {
  const [fromDate, setFromDate] = useState(isoToday());
  const [toDate, setToDate] = useState(isoDaysFromToday(14));
  const [busy, setBusy] = useState(false);
  const [sim, setSim] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(
          `/simulations/leave/latest?from_date=${fromDate}&to_date=${toDate}`);
        if (!cancelled) setSim(data);
      } catch (err) {
        if (err?.response?.status !== 404 && !cancelled)
          toast.error(formatError(err.response?.data?.detail) || "Failed to load simulation");
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async () => {
    if (toDate < fromDate) { toast.error("End date must be on or after start date."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/simulations/leave/run", {
        from_date: fromDate, to_date: toDate,
      });
      setSim(data);
      if (data.status === "completed") toast.success("Leave simulation complete");
      else toast.error(`Failed: ${data.error_message || "see history"}`);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Failed to run simulation");
    } finally { setBusy(false); }
  };

  const summary = sim?.result_summary || null;
  const raw = sim?.raw_result || null;
  const verdicts = raw?.verdicts || [];
  const limitations = raw?.limitations || [];

  return (
    <div className="space-y-5" data-testid="leave-sim-tab">
      {/* Help / limitation banner */}
      <div className="rounded-lg border border-[#BFD3FB] bg-[#F0F6FF] p-3 flex gap-3"
           data-testid="leave-sim-help">
        <Info className="h-4 w-4 text-[#1D4ED8] mt-0.5 flex-shrink-0" />
        <div className="text-xs text-[#1D4ED8] leading-relaxed">
          <span className="font-medium">What does this check?</span>{" "}
          For each pending leave request in the chosen window, NextRota
          virtually removes the employee from the rota and checks whether
          coverage, leave policies and required skills would still hold.
          These are <strong>guidance labels only</strong>, managers remain
          responsible for the final decision.
          <br />
          <span className="font-medium">Out of scope:</span> Leave balance is
          not checked in this simulation. Managers should confirm remaining
          entitlement separately.
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4 flex flex-wrap items-end gap-3"
           data-testid="leave-sim-controls">
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-[#64748B]" htmlFor="lv-from">From</Label>
          <Input id="lv-from" type="date" value={fromDate}
                 onChange={(e) => setFromDate(e.target.value)}
                 className="mt-1 w-44" data-testid="leave-sim-from" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-[#64748B]" htmlFor="lv-to">To</Label>
          <Input id="lv-to" type="date" value={toDate}
                 onChange={(e) => setToDate(e.target.value)}
                 className="mt-1 w-44" data-testid="leave-sim-to" />
        </div>
        <Button onClick={run} disabled={busy} data-testid="leave-sim-run"
                className="bg-[#2563EB] hover:bg-[#1D4ED8]">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
          {busy ? "Running…" : "Run simulation"}
        </Button>
        {sim?.created_at && (
          <div className="text-[11px] text-[#64748B] ml-auto">
            Last run: {new Date(sim.created_at).toLocaleString("en-GB", {
              day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false })}
            {sim.engine_version && (
              <span className="ml-2 inline-block text-[10px] uppercase tracking-widest text-[#1D4ED8] px-1.5 py-0.5 rounded bg-[#DBE7FE] border border-[#BFD3FB]">
                engine {sim.engine_version}
              </span>
            )}
          </div>
        )}
      </div>

      {!summary && !busy && (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-6 text-center"
             data-testid="leave-sim-empty">
          <Info className="h-7 w-7 text-[#2563EB] mx-auto mb-2" />
          <p className="text-sm font-medium text-[#0B1A36]">No leave simulation yet for this window</p>
          <p className="text-xs text-[#4B5773] mt-1">Pick a date range and click <strong>Run simulation</strong>.</p>
        </div>
      )}

      {summary && summary.setup_state && summary.setup_state !== "ready" && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex gap-3"
             data-testid={`leave-setup-state-${summary.setup_state}`}>
          <Info className="h-4 w-4 text-amber-700 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-amber-900">
            <span className="font-medium">Heads-up:</span>{" "}
            {summary.setup_state_message}
          </div>
        </div>
      )}

      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="leave-sim-kpis">
          <KpiTile label="Looks safe" value={summary.approve || 0} verdict="approve" />
          <KpiTile label="Needs review" value={summary.warn || 0} verdict="warn" />
          <KpiTile label="Likely should decline" value={summary.decline || 0} verdict="decline" />
        </div>
      )}

      {summary && verdicts.length === 0 && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-emerald-800 text-sm">
          No pending leave requests in this window.
        </div>
      )}

      {verdicts.length > 0 && (
        <div className="space-y-3" data-testid="leave-sim-verdicts">
          {verdicts.map((v) => <VerdictCard key={v.signature} v={v} />)}
        </div>
      )}

      {limitations.length > 0 && summary && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-[11px] text-[#475569]"
             data-testid="leave-sim-limitations">
          <div className="font-medium text-[#0B1A36] mb-1">What this check does not yet do</div>
          <ul className="list-disc list-inside space-y-0.5">
            {limitations.map((l) => <li key={l}>{l}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

function KpiTile({ label, value, verdict }) {
  const meta = VERDICT_META[verdict];
  return (
    <div className={`rounded-lg border p-4 ${meta.bg} ${meta.border}`}
         data-testid={`leave-kpi-${verdict}`}>
      <div className={`text-[11px] uppercase tracking-widest ${meta.colour}`}>{label}</div>
      <div className={`text-3xl font-display mt-1 ${meta.colour}`}>{value}</div>
      <div className="text-[11px] text-[#64748B] mt-1">Guidance label, manager decides.</div>
    </div>
  );
}

function VerdictCard({ v }) {
  const meta = VERDICT_META[v.verdict];
  const VerdictIcon = meta.Icon;
  return (
    <div className={`rounded-lg border ${meta.border} bg-white`}
         data-testid={`leave-verdict-${v.leave_request_id}`}>
      <div className={`flex items-center justify-between gap-3 px-4 py-2.5 ${meta.bg} border-b ${meta.border} rounded-t-lg`}>
        <div className="flex items-center gap-2">
          <VerdictIcon className={`h-4 w-4 ${meta.colour}`} />
          <span className={`text-sm font-medium ${meta.colour}`}>{meta.label}</span>
          <span className="text-[10px] uppercase tracking-widest text-[#64748B] ml-1">guidance only</span>
        </div>
        <div className="text-xs text-[#4B5773]">
          {v.employee_name || v.employee_id} · {fmtDate(v.from_date)}{v.from_date !== v.to_date ? ` → ${fmtDate(v.to_date)}` : ""}
        </div>
      </div>
      <div className="p-4 space-y-2">
        {v.blocking_findings.length > 0 && (
          <FindingsList rows={v.blocking_findings} colour="rose" />
        )}
        {v.warnings.length > 0 && (
          <FindingsList rows={v.warnings} colour="amber" />
        )}
        {v.positives.length > 0 && (
          <FindingsList rows={v.positives} colour="emerald" />
        )}
        {v.deep_links?.length > 0 && (
          <div className="flex flex-wrap gap-3 pt-1">
            {v.deep_links.map((dl) => (
              <a key={dl.href} href={dl.href}
                 className="text-xs text-[#1D4ED8] hover:underline inline-flex items-center gap-1">
                <ExternalLink className="h-3 w-3" />{dl.label}
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FindingsList({ rows, colour }) {
  const palette = {
    rose:    "text-rose-800 bg-rose-50 border-rose-200",
    amber:   "text-amber-800 bg-amber-50 border-amber-200",
    emerald: "text-emerald-800 bg-emerald-50 border-emerald-200",
  }[colour];
  return (
    <ul className="space-y-1">
      {rows.map((r, i) => {
        const Icon = ICON_BY_TYPE[r.type] || Info;
        return (
          <li key={i} className={`flex items-start gap-2 text-xs rounded border ${palette} px-2 py-1.5`}>
            <Icon className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
            <span>{r.text}</span>
          </li>
        );
      })}
    </ul>
  );
}

// Reusable inline pill for the /app/leave page.
export function LeaveSimulationPill({ leaveRequestId, onOpen }) {
  const [verdict, setVerdict] = useState(null);
  const [tried, setTried] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/simulations/leave/by-request/${leaveRequestId}`);
        if (!cancelled) setVerdict(data?.verdict);
      } catch {
        // No simulation yet, show neutral pill
      } finally {
        if (!cancelled) setTried(true);
      }
    })();
    return () => { cancelled = true; };
  }, [leaveRequestId]);

  if (!tried) return null;
  if (!verdict) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border bg-slate-50 border-slate-200 text-[#64748B]"
            data-testid={`leave-pill-${leaveRequestId}`}>
        Simulation: not run
      </span>
    );
  }
  const meta = VERDICT_META[verdict.verdict];
  const Icon = meta.Icon;
  return (
    <button type="button" onClick={() => onOpen?.(verdict)}
            className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${meta.bg} ${meta.border} ${meta.colour} hover:opacity-80`}
            data-testid={`leave-pill-${leaveRequestId}`}>
      <Icon className="h-3 w-3" />
      Simulation: {meta.label.toLowerCase()}
    </button>
  );
}
